import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Wand2 } from 'lucide-react';
import type { ChannelGroup, GroupLayout, ProbeInfo, SrtKeyLength, SrtMode, StreamSummary, StreamType } from '@shared/protocol';
import { api } from '../lib/api';
import { useDocumentTitle } from '../lib/branding';
import { useTargets } from '../lib/useTargets';
import { Segmented } from '../components/Panel';
import { Notice } from '../components/Status';

interface GroupRow {
  id: string;
  name: string;
  track: number; // 1-based in the form
  channels: string; // "1,2" 1-based
  layout: GroupLayout;
  presetId: number | null;
  main: boolean;
}

const layouts: { value: GroupLayout; label: string; n: number | null }[] = [
  { value: 'stereo', label: 'Stereo', n: 2 },
  { value: 'mono', label: 'Mono', n: 1 },
  { value: '5.1', label: '5.1 (L R C LFE Ls Rs)', n: 6 },
  { value: '5.0', label: '5.0 (L R C Ls Rs)', n: 5 },
  { value: 'multi', label: 'Other', n: null },
];

const toRow = (g: ChannelGroup): GroupRow => ({ id: g.id, name: g.name, track: g.track + 1, channels: g.channels.map((c) => c + 1).join(','), layout: g.layout, presetId: g.presetId, main: g.main });

/** Same rules as the server's automatic groups, as a starting point for editing. */
function groupsFromProbe(probe: ProbeInfo): GroupRow[] {
  const rows: GroupRow[] = [];
  probe.audio.slice(0, 8).forEach((a, ti) => {
    const multi = probe.audio.length > 1 ? `Track ${ti + 1} ` : '';
    const l = (a.channelLayout ?? '').toLowerCase();
    const layout: GroupLayout = a.channels === 1 ? 'mono' : a.channels === 2 ? 'stereo' : a.channels === 6 && (l.startsWith('5.1') || !l) ? '5.1' : a.channels === 5 ? '5.0' : 'multi';
    if (layout !== 'multi') {
      rows.push({ id: `g${ti}`, name: `${multi}${layout === 'stereo' ? 'Main stereo' : layout === 'mono' ? 'Mono' : `${layout} programme`}`, track: ti + 1, channels: Array.from({ length: a.channels }, (_, i) => i + 1).join(','), layout, presetId: null, main: rows.length === 0 });
      return;
    }
    for (let c = 0; c < a.channels; c += 2) {
      const pair = c + 1 < a.channels;
      rows.push({ id: `g${ti}-${c}`, name: `${multi}Ch ${c + 1}${pair ? `/${c + 2}` : ''}`, track: ti + 1, channels: pair ? `${c + 1},${c + 2}` : `${c + 1}`, layout: pair ? 'stereo' : 'mono', presetId: null, main: rows.length === 0 });
    }
  });
  return rows;
}

export function StreamFormPage() {
  const params = useParams();
  const editId = params.id ? Number(params.id) : null;
  useDocumentTitle(editId ? 'Edit stream' : 'Add stream');
  const navigate = useNavigate();
  const targets = useTargets();
  const [loaded, setLoaded] = useState(!editId);
  const [name, setName] = useState('');
  const [type, setType] = useState<StreamType>('hls');
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [targetIds, setTargetIds] = useState<number[]>([]);
  const [videoAnalysis, setVideoAnalysis] = useState(true);
  const [customGroups, setCustomGroups] = useState(false);
  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [probe, setProbe] = useState<ProbeInfo | null>(null);
  // SRT
  const [mode, setMode] = useState<SrtMode>('listener');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [localPort, setLocalPort] = useState('');
  const [latency, setLatency] = useState('200');
  const [passphrase, setPassphrase] = useState('');
  const [hasPassphrase, setHasPassphrase] = useState(false);
  const [removePassphrase, setRemovePassphrase] = useState(false);
  const [keyLength, setKeyLength] = useState<SrtKeyLength>(16);
  const [streamId, setStreamId] = useState('');
  const [destination, setDestination] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editId) return;
    api<{ stream: StreamSummary; probe: ProbeInfo | null }>('GET', `/api/streams/${editId}`)
      .then(({ stream: s, probe: p }) => {
        setName(s.name);
        setType(s.type);
        setNotes(s.notes);
        setTargetIds(s.config.targetIds);
        setVideoAnalysis(s.config.videoAnalysis);
        setCustomGroups(s.config.groups.length > 0);
        setGroups(s.config.groups.map(toRow));
        setProbe(p);
        if (s.config.hls) setUrl(s.config.hls.url);
        if (s.config.youtube) setUrl(s.config.youtube.url);
        if (s.config.srt) {
          const c = s.config.srt;
          setMode(c.mode);
          setHost(c.host);
          setPort(c.port ? String(c.port) : '');
          setLocalPort(c.localPort ? String(c.localPort) : '');
          setLatency(String(c.latencyMs));
          setHasPassphrase(c.hasPassphrase);
          setKeyLength(c.keyLength || 16);
          setStreamId(c.streamId);
          setDestination(c.destination);
        }
        setLoaded(true);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the stream.'));
  }, [editId]);

  const updateGroup = (i: number, patch: Partial<GroupRow>) =>
    setGroups((gs) => gs.map((g, j) => (j === i ? { ...g, ...patch } : patch.main ? { ...g, main: false } : g)));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const parsedGroups: ChannelGroup[] = customGroups
        ? groups.map((g, i) => {
            const channels = g.channels
              .split(/[\s,]+/)
              .filter(Boolean)
              .map((c) => Number(c) - 1);
            if (channels.some((c) => !Number.isInteger(c) || c < 0)) throw new Error(`Group ${i + 1}: list channels as numbers, e.g. 1,2.`);
            const want = layouts.find((l) => l.value === g.layout)?.n;
            if (want && channels.length !== want) throw new Error(`Group "${g.name}": a ${g.layout} group needs ${want} channel(s).`);
            return { id: g.id || `g${i}`, name: g.name.trim() || `Group ${i + 1}`, track: g.track - 1, channels, layout: g.layout, presetId: g.presetId, main: g.main };
          })
        : [];
      const body: Record<string, unknown> = { name, type, notes, targetIds, videoAnalysis, groups: parsedGroups };
      if (type === 'srt') {
        body.srt = {
          mode,
          host: mode === 'listener' ? '' : host,
          port: mode === 'listener' ? 0 : Number(port),
          localPort: mode !== 'caller' && localPort ? Number(localPort) : null,
          latencyMs: Number(latency),
          passphrase: removePassphrase ? '' : passphrase ? passphrase : undefined,
          keyLength,
          streamId,
          destination,
        };
      } else {
        body.url = url;
      }
      const r = editId ? await api<{ stream: StreamSummary }>('PATCH', `/api/streams/${editId}`, body) : await api<{ stream: StreamSummary }>('POST', '/api/streams', body);
      navigate(`/streams/${r.stream.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the stream.');
    } finally {
      setBusy(false);
    }
  };

  const broadcasters = targets.filter((t) => t.kind === 'broadcaster');
  const platforms = targets.filter((t) => t.kind === 'platform');
  const toggleTarget = (id: number) => setTargetIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  if (!loaded) {
    return <div className="page narrow">{error ? <Notice level="fail">{error}</Notice> : <p className="muted">Loading</p>}</div>;
  }

  return (
    <div className="page narrow">
      <div className="page-header">
        <div>
          <Link to={editId ? `/streams/${editId}` : '/'} className="back-link">
            <ArrowLeft size={14} aria-hidden /> {editId ? 'Back to the stream' : 'Streams'}
          </Link>
          <h1>{editId ? 'Edit stream' : 'Add stream'}</h1>
        </div>
      </div>
      <form onSubmit={submit} className="stream-form">
        <section className="card">
          <header className="card-header">
            <h2>Source</h2>
          </header>
          <div className="card-body">
            <div className="field">
              <label htmlFor="s-name">Name</label>
              <input id="s-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
            </div>
            <div className="field">
              <span className="label">Type</span>
              <Segmented<StreamType>
                label="Stream type"
                value={type}
                onChange={setType}
                options={[
                  { value: 'hls', label: 'HLS (.m3u8)' },
                  { value: 'youtube', label: 'YouTube Live' },
                  { value: 'srt', label: 'SRT' },
                ]}
              />
            </div>
            {type === 'hls' && (
              <div className="field">
                <label htmlFor="s-url">Playlist URL</label>
                <input id="s-url" className="input mono" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/live/master.m3u8" required />
                <span className="hint">Public http or https URL of a master or media playlist.</span>
              </div>
            )}
            {type === 'youtube' && (
              <div className="field">
                <label htmlFor="s-yt">YouTube link</label>
                <input id="s-yt" className="input mono" type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/live/VIDEO_ID" required />
                <span className="hint">A live video (youtube.com/live/..., watch?v=..., youtu.be/...) or a channel's live page (youtube.com/@channel/live). The stream must be public or unlisted. Note that YouTube re-encodes streams, so encoder settings (bitrate, keyframes) are checked on your SRT or HLS feed rather than here.</span>
              </div>
            )}
            {type === 'srt' && (
              <>
                <Notice level="info">
                  The monitor is a passive receiver: give it a duplicate of the feed (an extra SRT output from the encoder, or an output pulled from Castr). It never sits in the delivery chain to the broadcaster.
                </Notice>
                <div className="field" style={{ marginTop: 16 }}>
                  <span className="label">Mode</span>
                  <Segmented<SrtMode>
                    label="SRT mode"
                    value={mode}
                    onChange={setMode}
                    options={[
                      { value: 'listener', label: 'Listener (encoder calls us)' },
                      { value: 'caller', label: 'Caller (we connect)' },
                      { value: 'rendezvous', label: 'Rendezvous' },
                    ]}
                  />
                </div>
                <div className="form-grid">
                  {mode !== 'listener' && (
                    <>
                      <div className="field">
                        <label htmlFor="srt-host">Host</label>
                        <input id="srt-host" className="input mono" value={host} onChange={(e) => setHost(e.target.value)} placeholder="ingest.example.com" required />
                      </div>
                      <div className="field">
                        <label htmlFor="srt-port">Port</label>
                        <input id="srt-port" className="input mono" type="number" min={1} max={65535} value={port} onChange={(e) => setPort(e.target.value)} required />
                      </div>
                    </>
                  )}
                  {mode !== 'caller' && (
                    <div className="field">
                      <label htmlFor="srt-local">Local UDP port</label>
                      <input id="srt-local" className="input mono" type="number" min={1} max={65535} value={localPort} onChange={(e) => setLocalPort(e.target.value)} placeholder="automatic" />
                      <span className="hint">Leave empty to take the next free port from the configured range.</span>
                    </div>
                  )}
                  <div className="field">
                    <label htmlFor="srt-latency">Latency (ms)</label>
                    <input id="srt-latency" className="input mono" type="number" min={20} max={8000} value={latency} onChange={(e) => setLatency(e.target.value)} required />
                    <span className="hint">Start at about 3 to 4 times the round-trip time.</span>
                  </div>
                  <div className="field">
                    <label htmlFor="srt-pass">Passphrase</label>
                    <input
                      id="srt-pass"
                      className="input"
                      type="password"
                      autoComplete="new-password"
                      value={passphrase}
                      disabled={removePassphrase}
                      onChange={(e) => setPassphrase(e.target.value)}
                      placeholder={hasPassphrase ? 'Stored (leave empty to keep)' : 'None'}
                      minLength={10}
                      maxLength={79}
                    />
                    <span className="hint">10 to 79 characters. Stored encrypted; never shown again.</span>
                    {hasPassphrase && (
                      <label className="checkbox">
                        <input type="checkbox" checked={removePassphrase} onChange={(e) => setRemovePassphrase(e.target.checked)} /> Remove the stored passphrase
                      </label>
                    )}
                  </div>
                  <div className="field">
                    <label htmlFor="srt-key">Key length</label>
                    <select id="srt-key" className="input" value={keyLength} onChange={(e) => setKeyLength(Number(e.target.value) as SrtKeyLength)}>
                      <option value={16}>AES-128</option>
                      <option value={24}>AES-192</option>
                      <option value={32}>AES-256</option>
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="srt-sid">Stream ID</label>
                    <input id="srt-sid" className="input mono" value={streamId} onChange={(e) => setStreamId(e.target.value)} placeholder="e.g. #!::r=live/abc,m=request" />
                    <span className="hint">Needed for Castr and many broadcaster endpoints.</span>
                  </div>
                  <div className="field">
                    <label htmlFor="srt-dest">Destination or broadcaster</label>
                    <input id="srt-dest" className="input" value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g. Channel 4 via Castr" maxLength={120} />
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        <section className="card">
          <header className="card-header">
            <h2>Check against</h2>
          </header>
          <div className="card-body">
            <p className="muted small">Choose the broadcaster delivery specs and online platforms this stream goes to. Each gets its own compliance status.</p>
            <div className="target-columns">
              {[
                { title: 'Broadcaster presets', list: broadcasters },
                { title: 'Platform profiles', list: platforms },
              ].map((col) => (
                <fieldset key={col.title} className="target-list">
                  <legend>{col.title}</legend>
                  {col.list.map((t) => (
                    <label key={t.id} className="checkbox target-option">
                      <input type="checkbox" checked={targetIds.includes(t.id)} onChange={() => toggleTarget(t.id)} />
                      <span>
                        <strong>{t.name}</strong>
                        <span className="muted small">
                          {t.spec.loudness ? ` ${t.spec.loudness.integrated} LUFS, ${t.spec.loudness.maxTruePeak} dBTP` : ''}
                        </span>
                      </span>
                    </label>
                  ))}
                </fieldset>
              ))}
            </div>
          </div>
        </section>

        <section className="card">
          <header className="card-header">
            <h2>Audio channel groups</h2>
          </header>
          <div className="card-body">
            <Segmented<'auto' | 'custom'>
              label="Channel groups"
              value={customGroups ? 'custom' : 'auto'}
              onChange={(v) => {
                setCustomGroups(v === 'custom');
                if (v === 'custom' && groups.length === 0) setGroups(probe ? groupsFromProbe(probe) : [{ id: 'g0', name: 'Main stereo 1/2', track: 1, channels: '1,2', layout: 'stereo', presetId: null, main: true }]);
              }}
              options={[
                { value: 'auto', label: 'Automatic' },
                { value: 'custom', label: 'Custom mapping' },
              ]}
            />
            {!customGroups ? (
              <p className="muted small" style={{ marginTop: 10 }}>
                Stereo, mono and 5.1 tracks are measured as one group each; other multichannel tracks are split into pairs (1/2, 3/4, ...). The first group is the main programme.
              </p>
            ) : (
              <>
                <p className="muted small" style={{ marginTop: 10 }}>
                  Name each group, e.g. "Main stereo 1/2", "International sound 3/4", "Audio description 5/6". Loudness, true peak, phase and silence are measured per group against its preset. Platform profiles are checked against the main group.
                </p>
                <table className="table table-compact group-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Track</th>
                      <th>Channels</th>
                      <th>Layout</th>
                      <th>Preset</th>
                      <th>Main</th>
                      <th>
                        <span className="visually-hidden">Remove</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g, i) => (
                      <tr key={i}>
                        <td>
                          <input className="input input-inline" aria-label="Group name" value={g.name} onChange={(e) => updateGroup(i, { name: e.target.value })} />
                        </td>
                        <td>
                          <input className="input input-inline input-narrow" aria-label="Track" type="number" min={1} max={8} value={g.track} onChange={(e) => updateGroup(i, { track: Number(e.target.value) })} />
                        </td>
                        <td>
                          <input className="input input-inline input-narrow mono" aria-label="Channels" value={g.channels} onChange={(e) => updateGroup(i, { channels: e.target.value })} placeholder="1,2" />
                        </td>
                        <td>
                          <select className="input input-inline" aria-label="Layout" value={g.layout} onChange={(e) => updateGroup(i, { layout: e.target.value as GroupLayout })}>
                            {layouts.map((l) => (
                              <option key={l.value} value={l.value}>
                                {l.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select className="input input-inline" aria-label="Preset" value={g.presetId ?? ''} onChange={(e) => updateGroup(i, { presetId: e.target.value ? Number(e.target.value) : null })}>
                            <option value="">Stream default</option>
                            {broadcasters.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input type="radio" name="main-group" aria-label="Main programme" checked={g.main} onChange={() => updateGroup(i, { main: true })} />
                        </td>
                        <td>
                          <button type="button" className="btn btn-sm btn-ghost btn-icon btn-danger" aria-label={`Remove ${g.name}`} onClick={() => setGroups((gs) => gs.filter((_, j) => j !== i))}>
                            <Trash2 size={14} aria-hidden />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="row-actions">
                  <button type="button" className="btn btn-sm" onClick={() => setGroups((gs) => [...gs, { id: `g${Date.now() % 100000}`, name: `Group ${gs.length + 1}`, track: 1, channels: '', layout: 'stereo', presetId: null, main: gs.length === 0 }])}>
                    <Plus size={14} aria-hidden /> Add group
                  </button>
                  <button type="button" className="btn btn-sm" disabled={!probe} title={probe ? undefined : 'Available once the stream has been monitored'} onClick={() => probe && setGroups(groupsFromProbe(probe))}>
                    <Wand2 size={14} aria-hidden /> Detect from stream
                  </button>
                  {probe && (
                    <span className="muted small">
                      Detected: {probe.audio.map((a, i) => `track ${i + 1}: ${a.channels} ch${a.channelLayout ? ` (${a.channelLayout})` : ''}`).join(', ') || 'no audio'}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </section>

        <section className="card">
          <header className="card-header">
            <h2>Options</h2>
          </header>
          <div className="card-body">
            <label className="checkbox">
              <input type="checkbox" checked={videoAnalysis} onChange={(e) => setVideoAnalysis(e.target.checked)} /> Decode video for picture analysis (interlace detection, black, freeze, letterboxing, levels)
            </label>
            <p className="hint">Uses more CPU. Frame rate, keyframes and bitrate are measured either way.</p>
            <div className="field" style={{ marginTop: 16 }}>
              <label htmlFor="s-notes">Notes</label>
              <textarea id="s-notes" className="input" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={4000} />
            </div>
          </div>
        </section>

        {error && <Notice level="fail">{error}</Notice>}
        <div className="form-actions">
          <Link className="btn" to={editId ? `/streams/${editId}` : '/'}>
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {editId ? 'Save changes' : 'Add stream'}
          </button>
        </div>
      </form>
    </div>
  );
}
